import React from "react";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Switch from "react-switch";
import { userSelector } from "../../../slices/user/userSlice";
import UserState from "../../../slices/user/UserState";
import EmployeeNotificationPreferencesService, {
  EmployeeNotificationPreference
} from "../../../app/data/notifications/employeeNotificationPreferencesService";
import { QUERY_KEYS } from "../../../app/data/common/queryKeys";
import Loading from "../../../ui-components/loading/loading";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
// import { ReactComponent as CrossIcon } from '../../../images/cross.svg';
import check from "../../../images/check_white.svg";
import "./employeeNotificationPreferences.scss";

const sourceLabels: Record<string, string> = {
  EXCEPTION: "Exceptions",
  CLAIMS_SYSTEM: "Claims Updates",
  PICKUP: "Pickup Status Updates",
  PROBILL: "Shipment Status Updates"
};

const sourceGroups: Record<string, string[]> = {
  "Shipment Updates": ["PROBILL", "EXCEPTION"],
  "Claims": ["CLAIMS_SYSTEM"],
  "Pickup Notifications": ["PICKUP"]
};

const EmployeeNotificationPreferences: React.FC = () => {
  const userState: UserState = useSelector(userSelector);
  const queryClient = useQueryClient();
  const email = userState.profile?.email;

  const {
    data: preferences,
    isLoading,
    error
  } = useQuery({
    queryKey: [QUERY_KEYS.EMPLOYEE_NOTIFICATION_PREFERENCES, email],
    queryFn: async () => {
      const response = await EmployeeNotificationPreferencesService.getInstance().getPreferences(email!);
      if ((response as any).ok && (response as any).ok()) {
        return (response as any).data;
      }
      throw new Error((response as any).getError ? (response as any).getError() : "Failed to fetch preferences");
    },
    enabled: !!email,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ email, preference }: { email: string; preference: EmployeeNotificationPreference }) => {
      const response = await EmployeeNotificationPreferencesService.getInstance().updatePreference(email, preference);
      if ((response as any).ok && (response as any).ok()) {
        return response;
      }
      throw new Error((response as any).getError ? (response as any).getError() : "Failed to update preference");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMPLOYEE_NOTIFICATION_PREFERENCES, email] });
      toast.info("Notification preference updated", { autoClose: 2000 });
    },
    onError: (error: any) => {
      const message = error?.message || "Error updating preference";
      toast.error(message, { autoClose: 3000 });
    }
  });

  const handleToggle = (source: string, field: 'emailEnabled' | 'inappEnabled', checked: boolean) => {
    if (!email || !preferences) return;
    
    const currentPreference = preferences.find((p: EmployeeNotificationPreference) => p.source === source);
    if (!currentPreference) return;

    const updatedPreference: EmployeeNotificationPreference = {
      ...currentPreference,
      [field]: checked
    };

    updateMutation.mutate({ email, preference: updatedPreference });
  };

  if (isLoading) {
    return <Loading isLoading={true} />;
  }

  if (error) {
    return (
      <XGSErrorMessage>
        Failed to load notification preferences. Please try again later.
      </XGSErrorMessage>
    );
  }

  if (!preferences || preferences.length === 0) {
    return null;
  }

  const groupedPreferences: Record<string, EmployeeNotificationPreference[]> = {};
  Object.keys(sourceGroups).forEach(groupName => {
    groupedPreferences[groupName] = preferences.filter((p: EmployeeNotificationPreference) =>
      sourceGroups[groupName].includes(p.source)
    );
  });

  return (
    <div className="xgs-employee-notification-preferences">
      <div className="xgs-employee-notification-preferences__headers">
        <div className="xgs-employee-notification-preferences__header__name">Notification Type</div>
        <div className="xgs-employee-notification-preferences__header__control">Email</div>
        <div className="xgs-employee-notification-preferences__header__control">In-App</div>
      </div>
      {Object.keys(groupedPreferences).map((groupName) => (
        <div key={groupName} className="xgs-employee-notification-preferences__group">
          <div className="xgs-employee-notification-preferences__group__title">{groupName}</div>
          {groupedPreferences[groupName].map((preference: EmployeeNotificationPreference) => (
            <div
              key={preference.source}
              className="xgs-employee-notification-preferences__item"
            >
              <div className="xgs-employee-notification-preferences__item__name">
                {sourceLabels[preference.source] || preference.source}
              </div>
              <div className="xgs-employee-notification-preferences__item__control">
                <Switch
                  onColor="#2F80ED"
                  onChange={(checked) => handleToggle(preference.source, 'emailEnabled', checked)}
                  checked={preference.emailEnabled}
                  disabled={updateMutation.isLoading}
                  uncheckedIcon={
                    <div className="xgs-employee-notification-preferences__switch-icon">
                      {/* <CrossIcon style={{ color: 'white' }} /> */}
                    </div>
                  }
                  checkedIcon={
                    <div className="xgs-employee-notification-preferences__switch-icon">
                      <img src={check} alt="check" />
                    </div>
                  }
                />
                {/* <span className="xgs-employee-notification-preferences__coming-soon">Coming soon</span> */}
              </div>
              <div className="xgs-employee-notification-preferences__item__control">
                <Switch
                  onColor="#2F80ED"
                  onChange={(checked) => handleToggle(preference.source, 'inappEnabled', checked)}
                  checked={preference.inappEnabled}
                  disabled={updateMutation.isLoading}
                  uncheckedIcon={
                    <div className="xgs-employee-notification-preferences__switch-icon">
                      {/* <CrossIcon style={{ color: 'white' }} /> */}
                    </div>
                  }
                  checkedIcon={
                    <div className="xgs-employee-notification-preferences__switch-icon">
                      <img src={check} alt="check" />
                    </div>
                  }
                />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default EmployeeNotificationPreferences;

